/**
 * Hook for managing network multiplayer game sessions
 */

import { useState, useEffect, useCallback, useRef } from 'react'

export interface GameSession {
  id: number
  session_code: string
  game_type: string
  player1_name: string
  player2_name?: string
  game_state: any
  status: 'waiting' | 'active' | 'finished'
  created_at: string
  updated_at: string
}

export interface GameMove {
  player_number: 1 | 2
  move_data: any
  created_at: string
}

export interface ChatMessage {
  player_number: 1 | 2
  player_name: string
  message: string
  created_at: string
}

interface UseGameSessionOptions {
  enablePolling?: boolean // If false, disables all polling (for when PeerJS is active)
}

export function useGameSession(sessionCode?: string, options: UseGameSessionOptions = {}) {
  const { enablePolling = true } = options

  const [session, setSession] = useState<GameSession | null>(null)
  const [moves, setMoves] = useState<GameMove[]>([])
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastFetch, setLastFetch] = useState<string>(new Date().toISOString())
  const consecutiveErrorsRef = useRef(0)
  const isPollingRef = useRef(false)

  // Create a new game session
  const createSession = useCallback(async (gameType: string, playerName: string) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gameType, playerName }),
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to create session')
      }

      setSession(data.data)
      return data.data as GameSession
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create session'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Join an existing session
  const joinSession = useCallback(async (code: string, playerName: string) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/sessions/${code}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerName }),
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to join session')
      }

      setSession(data.data)
      return data.data as GameSession
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to join session'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch session details
  const fetchSession = useCallback(async (code: string) => {
    try {
      const response = await fetch(`/api/sessions/${code}`)
      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Session not found')
      }

      setSession(data.data)
      return data.data as GameSession
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch session'
      setError(message)
      return null
    }
  }, [])

  // Submit a move
  const submitMove = useCallback(async (
    code: string,
    playerNumber: 1 | 2,
    moveData: any,
    gameState?: any
  ) => {
    try {
      const response = await fetch(`/api/sessions/${code}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerNumber, moveData, gameState }),
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to submit move')
      }

      // Refresh session and moves
      await fetchSession(code)
      setLastFetch(new Date().toISOString())

      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit move'
      setError(message)
      return false
    }
  }, [fetchSession])

  // Poll for session updates (for both moves and player joins)
  useEffect(() => {
    if (!sessionCode || !enablePolling) {
      console.log('[useGameSession] Polling disabled:', { sessionCode, enablePolling })
      return
    }

    console.log('[useGameSession] ✅ Polling ENABLED (lobby/waiting or PeerJS disconnected)')

    const pollSession = async () => {
      // Prevent concurrent polling requests
      if (isPollingRef.current) {
        return
      }

      isPollingRef.current = true

      try {
        // Fetch session updates
        const response = await fetch(`/api/sessions/${sessionCode}`)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (data.success) {
          // Reset error counter on success
          consecutiveErrorsRef.current = 0

          setSession(prev => {
            // Only update if something changed to prevent unnecessary re-renders
            if (JSON.stringify(prev) !== JSON.stringify(data.data)) {
              return data.data
            }
            return prev
          })

          // Fetch new moves if session is active
          if (data.data.status !== 'finished') {
            try {
              const movesResponse = await fetch(
                `/api/sessions/${sessionCode}/move?since=${encodeURIComponent(lastFetch)}`
              )

              if (movesResponse.ok) {
                const movesData = await movesResponse.json()

                if (movesData.success && movesData.data && movesData.data.length > 0) {
                  setMoves(prev => [...prev, ...movesData.data])
                  setLastFetch(new Date().toISOString())
                }
              } else {
                console.warn(`Moves fetch failed: HTTP ${movesResponse.status}`)
              }
            } catch (moveErr) {
              // Don't fail the entire polling if moves fetch fails
              console.error('Moves fetch error:', moveErr)
            }
          }
        }
      } catch (err) {
        consecutiveErrorsRef.current++
        console.error(`Polling error (${consecutiveErrorsRef.current} consecutive):`, err)

        // Stop polling after 3 consecutive errors to prevent server overload
        if (consecutiveErrorsRef.current >= 3) {
          console.error('Too many polling errors, stopping polling')
          setError('Connection lost. Please refresh the page.')
          clearInterval(interval)
        }
      } finally {
        isPollingRef.current = false
      }
    }

    // Poll every 5 seconds (reduced from 3s to lower load)
    const interval = setInterval(pollSession, 5000)

    // Initial poll with 1 second delay to allow session creation to complete
    const initialTimeout = setTimeout(pollSession, 1000)

    return () => {
      clearInterval(interval)
      clearTimeout(initialTimeout)
    }
  }, [sessionCode, lastFetch, enablePolling])

  // Send a chat message
  const sendChatMessage = useCallback(async (
    code: string,
    playerNumber: 1 | 2,
    playerName: string,
    message: string
  ) => {
    try {
      const response = await fetch(`/api/sessions/${code}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerNumber, playerName, message }),
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to send message')
      }

      // Add message to local state immediately
      const newMessage: ChatMessage = {
        player_number: playerNumber,
        player_name: playerName,
        message,
        created_at: new Date().toISOString()
      }
      setChatMessages(prev => [...prev, newMessage])

      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send message'
      setError(message)
      return false
    }
  }, [])

  // Fetch chat messages
  const fetchChatMessages = useCallback(async (code: string) => {
    try {
      const response = await fetch(`/api/sessions/${code}/chat`)
      const data = await response.json()

      if (data.success) {
        setChatMessages(data.data)
      }
    } catch (err) {
      console.error('Failed to fetch chat messages:', err)
    }
  }, [])

  // Poll for new chat messages
  useEffect(() => {
    if (!sessionCode || !enablePolling) return

    console.log('[useGameSession] Chat polling ENABLED')

    // Initial fetch with 1 second delay
    const initialTimeout = setTimeout(() => {
      fetchChatMessages(sessionCode)
    }, 1000)

    // Poll every 5 seconds (reduced from 3s)
    const interval = setInterval(() => {
      fetchChatMessages(sessionCode)
    }, 5000)

    return () => {
      clearInterval(interval)
      clearTimeout(initialTimeout)
    }
  }, [sessionCode, fetchChatMessages, enablePolling])

  // Send heartbeat to mark player as connected
  const sendHeartbeat = useCallback(async (code: string, playerNumber: 1 | 2) => {
    try {
      const response = await fetch(`/api/sessions/${code}/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerNumber }),
      })

      if (!response.ok) {
        // Silently fail if heartbeat endpoint not available (e.g., database not migrated)
        return false
      }

      const data = await response.json()

      if (!data.success) {
        // Silently fail - heartbeat is optional feature
        return false
      }

      return data.data
    } catch (err) {
      console.error('Heartbeat error:', err)
      return false
    }
  }, [])

  // Rejoin a session after disconnect
  const rejoinSession = useCallback(async (code: string, playerName: string) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/sessions/${code}/rejoin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerName }),
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to rejoin session')
      }

      setSession(data.data)
      return { session: data.data as GameSession, playerNumber: data.playerNumber as 1 | 2 }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to rejoin session'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    session,
    moves,
    chatMessages,
    loading,
    error,
    createSession,
    joinSession,
    rejoinSession,
    fetchSession,
    submitMove,
    sendChatMessage,
    sendHeartbeat,
  }
}
